import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
const IDLE_MS = 15 * 60 * 1000; // 15 minutes of true inactivity before warning
const WARNING_SECONDS = 10;
const WARNING_MS = WARNING_SECONDS * 1000;
const PING_THROTTLE_MS = 60 * 1000; // at most one activity ping per minute
// Keeps the underlying session cookie/JWT (1h absolute lifetime — see
// backend/src/lib/sessionConfig.js) from silently expiring out from under
// someone who's genuinely still active. CONFIRMED (2026-09-01): nothing
// else in the app re-mints it after initial login/mount, so without this,
// an actively-used session eventually hits that 1h wall clock and
// AuthContext.js's own 30s watchdog force-redirects to '/' with NO warning
// at all — looks exactly like a random early logout with no popup,
// completely independent of IDLE_MS above. Comfortably under the 1h cap so
// continued real activity (gated the same way pingActivity is below) keeps
// renewing it well before that; a genuinely idle session still expires on
// schedule via the warning/logout flow instead of being kept alive forever.
const SESSION_REFRESH_MS = 20 * 60 * 1000;
// Same cadence as AuthContext.js's existing session-status watchdog —
// piggybacks on an already-established polling rhythm rather than adding
// a second, differently-timed interval.
const SHARED_CHECK_MS = 30 * 1000;
// Single source of truth for idle/warning/logout state is a frequent
// timestamp comparison (see checkState() below), not a precisely-scheduled
// long-delay timer — see that function's own comment for why.
const TICK_MS = 1000;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(20, 24, 33, 0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '20px', zIndex: 2000,
};
const cardStyle = {
  background: '#fff', borderRadius: '14px', padding: '28px',
  width: '100%', maxWidth: '380px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
  textAlign: 'center',
};

// Idle-timeout warning, backed by cross-app shared activity (see
// backend/src/routes/activity.js for why this can't just watch this
// tab's own DOM events in isolation — someone actively using a different
// connected app shouldn't get logged out just because THIS tab sat idle).
//
// Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
// header comment) — `user` (from useAuth(), populated via GET
// /account/me) is now the only source of truth for "is someone logged
// in" available here; the cookie itself can't be read directly. Every
// ping/status call below just sends credentials: 'include' instead of a
// manually-read token.
export default function IdleTimeoutWatcher() {
  const { user, logout, refreshUser } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);

  const lastKnownActivityRef = useRef(Date.now());
  const lastPingSentRef = useRef(0);
  const lastRefreshSentRef = useRef(Date.now());
  const warningShownAtRef = useRef(null);
  const showWarningRef = useRef(false);
  // CONFIRMED BUG (2026-09-08): guards against checkState() re-triggering
  // logout() on every subsequent 1s tick after the first one already
  // fired. Nothing previously reset warningShownAtRef/showWarningRef once
  // logout() was called — `now - null` coerces to `now` itself in JS
  // arithmetic, so the very next tick's `WARNING_MS - (now - null)` still
  // reads as deeply negative ("time's up") and fired logout() AGAIN, every
  // second, for as long as it took the actual page navigation inside
  // logout() to complete — confirmed live via duplicate Activity Log
  // entries for the same idle timeout a moment apart, the second missing
  // its session-duration text because the first call had already cleared
  // it server-side by the time the second one looked it up.
  const loggedOutRef = useRef(false);
  // logout/refreshUser mirrored into refs (same pattern as showWarningRef
  // below, and userRef in AuthContext.js) so the main effect's dependency
  // array can stay [user] alone — logout in particular is a new function
  // reference on every AuthProvider render, and this effect re-subscribing
  // its DOM listeners + intervals every time that happens would be wasteful.
  const logoutRef = useRef(logout);
  const refreshUserRef = useRef(refreshUser);
  // Populated inside the effect below so the "Continue" button (outside
  // that effect's closure) can trigger the exact same reset logic.
  const handleActivityRef = useRef(() => {});

  useEffect(() => { showWarningRef.current = showWarning; }, [showWarning]);
  useEffect(() => { logoutRef.current = logout; }, [logout]);
  useEffect(() => { refreshUserRef.current = refreshUser; }, [refreshUser]);

  useEffect(() => {
    if (!user) return undefined;

    // CONFIRMED BUG (2026-11-02): lastKnownActivityRef's useRef(Date.now())
    // initializer only ever runs at this component's first render — which
    // happens at SPA mount (App.js renders this unconditionally, outside
    // <Routes>), NOT at the moment `user` actually becomes available. For
    // most logins the gap between those two moments is a couple of
    // seconds, harmless — but for someone going through registration (the
    // page sits open through phone/email OTP entry, then scanning a TOTP
    // QR code and typing the result back — genuinely multi-minute), the
    // idle clock had silently been running the whole time since before
    // they were even logged in. By the time they reached the dashboard,
    // most of the 15-minute budget was already gone, so idle-timeout fired
    // within a few minutes of real, continuous use — confirmed live via
    // several new accounts' Activity Log entries (`idle timeout`, 1–6 min
    // session durations) alongside unbroken tab-presence heartbeats
    // straight through the logout. Resetting here, the moment this effect
    // actually starts watching, gives every session its real, full 15
    // minutes from when the person is genuinely logged in.
    lastKnownActivityRef.current = Date.now();

    function pingActivity() {
      const now = Date.now();
      if (now - lastPingSentRef.current < PING_THROTTLE_MS) return;
      lastPingSentRef.current = now;
      fetch(`${API_BASE}/account/activity/ping`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    }

    function extendSessionIfNeeded() {
      const now = Date.now();
      if (now - lastRefreshSentRef.current < SESSION_REFRESH_MS) return;
      lastRefreshSentRef.current = now;
      refreshUserRef.current();
    }

    function handleActivity() {
      lastKnownActivityRef.current = Date.now();
      pingActivity();
      extendSessionIfNeeded();
      if (showWarningRef.current) {
        warningShownAtRef.current = null;
        setShowWarning(false);
      }
    }
    handleActivityRef.current = handleActivity;

    // CONFIRMED (2026-09-01): a plain setTimeout(IDLE_MS) here used to
    // silently miss its deadline on a backgrounded/minimized tab (browsers
    // throttle or fully suspend timers in hidden tabs/windows), then fire
    // all at once the moment the tab regained focus — the "10-second
    // countdown suddenly starts the moment I switch back" symptom, plus
    // the "waited 15 minutes, no popup" symptom when it never got a chance
    // to catch up before the person gave up and left. Comparing timestamps
    // on a frequent tick (TICK_MS), PLUS an immediate re-check on
    // visibilitychange/focus below, means the state shown is always
    // correct for how much time has actually passed — including skipping
    // straight to a fresh warning (not a silent logout) if a lot of real
    // time already elapsed while the tab was away, same grace period
    // either way.
    function checkState() {
      if (loggedOutRef.current) return;
      const now = Date.now();
      if (!showWarningRef.current) {
        const idleFor = now - lastKnownActivityRef.current;
        if (idleFor < IDLE_MS) return;
        // CONFIRMED BUG (2026-09-08): this used to unconditionally start a
        // FRESH WARNING_SECONDS countdown right here, the moment IDLE_MS
        // was first noticed crossed — correct if that's genuinely just now
        // happening, but wrong on a backgrounded/minimized tab (browsers
        // throttle timers there, same reasoning as the file header comment
        // above): checkState() might not run again until the tab regains
        // focus, by which point idleFor could already be well past
        // IDLE_MS + WARNING_MS. Restarting a fresh 10s countdown at THAT
        // point silently extends the real total idle time by up to
        // WARNING_MS on every late check-in, and can repeat indefinitely
        // if someone glances at a still-backgrounded tab without actually
        // interacting — exactly the "waited way more than 15 minutes,
        // still got a fresh 10-second countdown instead of an immediate
        // logout" symptom this fixes. intoWarning below measures how far
        // PAST the idle threshold we already are; only show a countdown
        // for whatever's genuinely still left in the warning window, and
        // log out immediately if that window's already fully elapsed.
        const intoWarning = idleFor - IDLE_MS;
        if (intoWarning >= WARNING_MS) {
          loggedOutRef.current = true;
          logoutRef.current('idle');
          return;
        }
        warningShownAtRef.current = now - intoWarning;
        setSecondsLeft(Math.max(1, Math.ceil((WARNING_MS - intoWarning) / 1000)));
        setShowWarning(true);
        return;
      }
      const remainingMs = WARNING_MS - (now - warningShownAtRef.current);
      if (remainingMs <= 0) {
        warningShownAtRef.current = null;
        loggedOutRef.current = true;
        // 'idle' — see AuthContext.js's own logout() comment; labels this
        // entry distinctly from a plain manual logout in the Activity Log.
        logoutRef.current('idle');
        return;
      }
      setSecondsLeft(Math.ceil(remainingMs / 1000));
    }

    function handleVisibilityOrFocus() {
      if (document.visibilityState === 'visible') checkState();
    }

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    const tickId = setInterval(checkState, TICK_MS);

    // Cross-app check: has ANY connected app seen this person recently,
    // even if this particular tab hasn't?
    const sharedCheckId = setInterval(() => {
      fetch(`${API_BASE}/account/activity/status`, {
        credentials: 'include',
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.lastActivity && data.lastActivity > lastKnownActivityRef.current) {
            lastKnownActivityRef.current = data.lastActivity;
            extendSessionIfNeeded();
            if (showWarningRef.current) {
              warningShownAtRef.current = null;
              setShowWarning(false);
            }
          }
        })
        .catch(() => {});
    }, SHARED_CHECK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      clearInterval(tickId);
      clearInterval(sharedCheckId);
    };
  }, [user]);

  if (!user || !showWarning) return null;

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 10px' }}>
          Still there?
        </h3>
        <p style={{ fontSize: '14px', color: '#5F5E5A', margin: '0 0 20px', lineHeight: 1.5 }}>
          You've been inactive for a while. For your security, you'll be signed out in{' '}
          <strong>{secondsLeft}</strong> second{secondsLeft === 1 ? '' : 's'}.
        </p>
        <button
          type="button"
          onClick={() => handleActivityRef.current()}
          style={{
            padding: '10px 28px', background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
            color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Continue session
        </button>
      </div>
    </div>
  );
}
